/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Text } from 'grommet';
import { TextInput } from '@blend/alchemy';

import { fullName, joinName } from 'blb-utils/format';
import { StyledFieldRow, StyledNameField, StyledMiddleNameField, StyledSuffixNameField } from './styles';
import { EditNamePreview } from './EditNamePreview';

export namespace EditRecipientRow {
  export interface Props {
    onChange: (update: BlendBridgeInternal.API.PatchRecipientOverride, isValid: boolean) => void;
    recipient: BlendBridgeInternal.API.EsignRecipient;
  }
}

export const EditRecipientRow: React.FC<EditRecipientRow.Props> = ({ recipient, onChange }) => {
  const [firstName, setFirstName] = useState<string>(recipient.firstName);
  const [middleName, setMiddleName] = useState<string>(recipient.middleName);
  const [lastName, setLastName] = useState<string>(recipient.lastName);
  const [suffixName, setSuffixName] = useState<string>(recipient.suffixName);

  const getValidationError = (value: string, nameType: 'First' | 'Last') => {
    if (!value) {
      return `${nameType} name is required`;
    }
    if (value.match(/^\s/)) {
      return `${nameType} name cannot start with whitespace`;
    }
  };

  useEffect(() => {
    const update = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      middleName: middleName.trim(),
      partyId: recipient.partyId,
      suffixName: suffixName.trim(),
    };
    const isValid = !getValidationError(firstName, 'First') && !getValidationError(lastName, 'Last');
    onChange(update, isValid);
  }, [firstName, middleName, lastName, suffixName]);

  return (
    <Box>
      <Text as="p" id={`it-recipientNameLabel-${recipient.partyId}`}>
        <Text weight="bold">{fullName(recipient)}</Text> ({recipient.email})
      </Text>
      <Text>eSignature display name</Text>
      <StyledFieldRow direction="row" flex justify="between" margin={{ bottom: 'none' }}>
        <StyledNameField
          label={<Text size="small">First name</Text>}
          name={`${recipient.partyId}-first-name`}
          validate={(value: string) => getValidationError(value, 'First')}
        >
          <TextInput
            id={`it-${recipient.partyId}-first-name`}
            label="First name"
            name={`${recipient.partyId}-first-name`}
            onChange={e => setFirstName(e.target.value)}
            showLabel={false}
            value={firstName}
          />
        </StyledNameField>
        <StyledMiddleNameField label={<Text size="small">Middle name</Text>} name={`${recipient.partyId}-middle-name`}>
          <TextInput
            id={`it-${recipient.partyId}-middle-name`}
            label="Middle name"
            name={`${recipient.partyId}-middle-name`}
            onChange={e => setMiddleName(e.target.value)}
            showLabel={false}
            value={middleName}
          />
        </StyledMiddleNameField>
        <StyledNameField
          label={<Text size="small">Last name</Text>}
          name={`${recipient.partyId}-last-name`}
          validate={(value: string) => getValidationError(value, 'Last')}
        >
          <TextInput
            id={`it-${recipient.partyId}-last-name`}
            label="Last name"
            name={`${recipient.partyId}-last-name`}
            onChange={e => setLastName(e.target.value)}
            showLabel={false}
            value={lastName}
          />
        </StyledNameField>
        <StyledSuffixNameField label={<Text size="small">Suffix</Text>} name={`${recipient.partyId}-suffix-name`}>
          <TextInput
            id={`it-${recipient.partyId}-suffix-name`}
            label="Suffix"
            name={`${recipient.partyId}-suffix-name`}
            onChange={e => setSuffixName(e.target.value)}
            showLabel={false}
            value={suffixName}
          />
        </StyledSuffixNameField>
      </StyledFieldRow>
      <EditNamePreview
        firstName={joinName(firstName, middleName)}
        lastName={joinName(lastName, suffixName)}
        partyId={recipient.partyId}
      />
    </Box>
  );
};
